import { motion } from "framer-motion";
import { Package, User } from "lucide-react";
import { FaClipboardList, FaTasks } from "react-icons/fa";
import { MdDashboard } from "react-icons/md";
import { useLocation, useNavigate } from "react-router-dom";
import { PiCodesandboxLogoLight } from "react-icons/pi";
import { useAdminAuth } from "../../../../context/AdminAuthContext";

const staffBottomNavRoutes = [
  {
    label: "Dashboard",
    path: "/dashboard-staff",
    icon: MdDashboard,
    key: "dashboard",
  },
  {
    label: "My Jobs",
    path: "/my-jobs",
    icon: FaTasks,
    key: "my-jobs",
  },
  {
    label: "Profile",
    path: "/staff-profile",
    icon: User,
    key: "profile",
    isCenter: true,
  },
  {
    label: "Products",
    path: "/all-products",
    icon: PiCodesandboxLogoLight,
    key: "all-products",
  },
  {
    label: "Requests",
    path: "/request-stats",
    icon: FaClipboardList,
    key: "request-stats",
  },
];

const StaffBottomNav = () => {
  const { adminRole, admin } = useAdminAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Only show for staff users
  const isStaff = adminRole === "staff" || localStorage.getItem("adminRole") === "staff";
  if (!isStaff) return null;

  const isActive = (item) => {
    const currentPath = location.pathname;
    // Exact match
    if (currentPath === item.path) return true;
    // Prefix match for nested routes
    return currentPath.startsWith(item.path + "/");
  };

  const adminName = admin?.name || "User";
  const initials = adminName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-gray-200 shadow-lg md:hidden">
      <div className="flex justify-around items-center px-2 py-2">
        {staffBottomNavRoutes.map((item) => {
          const Icon = item.icon;
          const active = isActive(item);

          if (item.isCenter) {
            return (
              <button
                key={item.key}
                onClick={() => navigate(item.path)}
                className="relative flex flex-col items-center justify-center min-w-[25px] -mt-11 transition-all active:scale-95"
              >
                {/* Elevated Circle Background */}
                <div className="relative">
                  {/* Outer glow ring */}
                  <div className="absolute inset-0 bg-gradient-to-r from-brandAccentColor to-accentTextColor rounded-full blur-md opacity-30 scale-110" />

                  {/* Main circle with profile pic or initials */}
                  <div className="relative w-12 h-12 rounded-full bg-gradient-to-br from-brandAccentColor to-accentTextColor shadow-lg flex items-center justify-center overflow-hidden border-4 border-white">
                    {admin?.profilePicture ? (
                      <img
                        src={admin.profilePicture}
                        alt={adminName}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-white font-bold text-lg">
                        {initials}
                      </span>
                    )}
                  </div>
                </div>

                {/* Label */}
                {/* <span className="mt-1 text-[10px] font-medium text-accentTextColor">
                  {item.label}
                </span> */}
              </button>
            );
          }

          // Regular nav items
          return (
            <button
              key={item.key}
              onClick={() => navigate(item.path)}
              className="relative flex flex-col items-center justify-center min-w-[70px] py-1 px-1.5 rounded-xl transition-all active:scale-95"
            >
              {/* Active Background */}
              {active && (
                <motion.div
                  layoutId="activeTabBg"
                  className="absolute inset-0 bg-gradient-to-r from-brandAccentColor/10 to-accentTextColor/10 rounded-xl"
                  transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                />
              )}

              {/* Icon Container */}
              <div className="relative z-10">
                <div
                  className={`p-1 rounded-lg transition-all duration-200 ${active
                    ? "bg-gradient-to-r from-brandAccentColor to-accentTextColor shadow-md"
                    : "bg-transparent"
                    }`}
                >
                  <Icon
                    className={`transition-all text-base duration-200 ${active ? "text-white" : "text-gray-500"
                      }`}
                  />
                </div>
              </div>

              {/* Label */}
              <span
                className={`relative z-10 text-[12px] sm:text-[13px] font-medium transition-all duration-200 ${active ? "text-accentTextColor" : "text-gray-600"
                  }`}
              >
                {item.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Safe area spacer for iOS devices */}
      <div className="h-[env(safe-area-inset-bottom)]" />
    </nav>
  );
};

export default StaffBottomNav;